import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { fuelDefaults } from '@/data/defaults'
import { calculateFuelCost } from './calculation'
import { content, faqs } from './content'
import type { FuelCostInputs, FuelCostResult } from './types'

const schema = z.object({
  distanceKm: positiveMoney('Enter a distance').refine((v) => v > 0, {
    message: 'Distance must be greater than 0',
  }),
  consumptionLPer100km: z
    .number()
    .min(0.1, { message: 'Consumption must be at least 0.1 L/100km' }),
  fuelPricePerLitre: positiveMoney('Enter a fuel price').refine((v) => v > 0, {
    message: 'Fuel price must be greater than 0',
  }),
  tripsPerPeriod: z.number().min(1).max(1000),
  period: z.enum(['day', 'week', 'month', 'year']),
})

const defaults: FuelCostInputs = {
  distanceKm: 30,
  consumptionLPer100km: 7.5,
  fuelPricePerLitre: fuelDefaults.defaultFuelPrice,
  tripsPerPeriod: 20,
  period: 'month',
}

export const fuelCostDefinition: CalculatorDefinition<FuelCostInputs, FuelCostResult> = {
  id: 'fuel-cost',
  slug: 'fuel-cost-calculator',
  category: 'vehicle',
  title: 'Fuel Cost Calculator',
  shortTitle: 'Fuel Cost',
  description:
    'Estimate trip, monthly and annual fuel costs in South Africa. Override the default fuel price with the latest local figure.',
  inputs: [
    {
      name: 'distanceKm',
      label: 'Trip distance',
      type: 'number',
      required: true,
      unit: 'km',
      min: 0,
      step: 0.1,
    },
    {
      name: 'consumptionLPer100km',
      label: 'Fuel consumption',
      type: 'number',
      required: true,
      unit: 'L/100km',
      min: 0.1,
      step: 0.1,
    },
    {
      name: 'fuelPricePerLitre',
      label: 'Fuel price',
      type: 'money',
      required: true,
      unit: 'R/L',
      min: 0,
      step: 0.01,
      defaultValue: fuelDefaults.defaultFuelPrice,
    },
    {
      name: 'tripsPerPeriod',
      label: 'Trips per period',
      type: 'number',
      required: true,
      min: 1,
      step: 1,
      defaultValue: 20,
    },
    {
      name: 'period',
      label: 'Period',
      type: 'radio',
      required: true,
      options: [
        { value: 'day', label: 'Day' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
        { value: 'year', label: 'Year' },
      ],
      defaultValue: 'month',
    },
  ],
  defaults,
  schema,
  calculate: calculateFuelCost,
  resultSections: [
    {
      id: 'costPerPeriod',
      label: 'Cost per period',
      format: 'currency',
      value: (r: FuelCostResult) => r.costPerPeriod,
      highlighted: true,
    },
    {
      id: 'costPerTrip',
      label: 'Cost per trip',
      format: 'currency',
      value: (r: FuelCostResult) => r.costPerTrip,
    },
    {
      id: 'annualCost',
      label: 'Annual cost',
      format: 'currency',
      value: (r: FuelCostResult) => r.annualCost,
    },
    {
      id: 'litresPerTrip',
      label: 'Litres per trip',
      format: 'number',
      value: (r: FuelCostResult) => r.litresPerTrip,
    },
    {
      id: 'annualLitres',
      label: 'Annual litres',
      format: 'number',
      value: (r: FuelCostResult) => r.annualLitres,
    },
  ],
  assumptions: [
    {
      name: fuelDefaults.sourceName,
      url: fuelDefaults.sourceUrl,
      reviewedAt: '2026-09-11',
      effectiveFrom: fuelDefaults.dataDate,
      version: fuelDefaults.dataDate,
      notes: `Default fuel price set to R${fuelDefaults.defaultFuelPrice} per litre. Always update it to your local pump price.`,
    },
  ],
  related: ['car-affordability-calculator', 'salary-calculator'],
  seo: {
    title: 'Fuel Cost Calculator South Africa',
    description:
      'Estimate petrol cost per km, per trip and monthly fuel cost with this fuel cost calculator for South Africa — editable pump price.',
    keywords: ['fuel cost calculator', 'petrol cost', 'South Africa fuel', 'trip cost'],
  },
  content,
  faqs,
}
